import { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

export default function PatientSchedule() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [assignments, setAssignments] = useState([])
  const [interventionAssignments, setInterventionAssignments] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchData() }, [])

  async function fetchData() {
    const [{ data: assignmentData }, { data: interventionData }] = await Promise.all([
      supabase.from('assignments').select('*, exercises(*)').eq('patient_id', profile.id).order('sort_order'),
      supabase.from('intervention_assignments').select('*, interventions(*)').eq('patient_id', profile.id).order('sort_order')
    ])
    setAssignments(assignmentData || [])
    setInterventionAssignments(interventionData || [])
    setLoading(false)
  }

  function isScheduledOnDay(a, dayIndex) {
    if (a.frequency === 'daily') return true
    if (a.frequency === 'custom' && a.days_of_week) return a.days_of_week.includes(dayIndex)
    return true
  }

  function endDateLabel(a) {
    if (!a.num_weeks || !a.assigned_at) return null
    const end = new Date(a.assigned_at)
    end.setDate(end.getDate() + a.num_weeks * 7)
    return MONTH_LABELS[end.getMonth()] + ' ' + end.getDate()
  }

  const programEnd = (() => {
    const allItems = [...assignments, ...interventionAssignments]
    const ends = allItems.map(a => {
      if (!a.num_weeks || !a.assigned_at) return null
      const end = new Date(a.assigned_at)
      end.setDate(end.getDate() + a.num_weeks * 7)
      return end
    }).filter(Boolean)
    if (ends.length === 0) return null
    return new Date(Math.max(...ends.map(d => d.getTime())))
  })()

  const startDate = assignments[0]?.assigned_at || interventionAssignments[0]?.assigned_at

  if (loading) return <><Navbar /><div className="page"><p>Loading...</p></div></>

  const cellStyle = {
    border: '1px solid #e5e7eb',
    padding: '8px 6px',
    textAlign: 'center',
    fontSize: 12,
    verticalAlign: 'middle'
  }

  const headerCellStyle = {
    ...cellStyle,
    background: '#f9fafb',
    fontWeight: 700,
    fontSize: 11,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5
  }

  return (
    <>
      <div className="no-print"><Navbar /></div>

      <div className="page">
        <div className="no-print" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span onClick={() => navigate('/patient')} className="back-link" style={{ cursor: 'pointer' }}>← Back</span>
          <button onClick={() => window.print()} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            🖨️ Print / Save PDF
          </button>
        </div>

        <div id="print-area">

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '3px solid #6366f1' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                {profile?.clinics?.logo_url && (
                  <img src={profile.clinics.logo_url} alt={profile.clinics.name} style={{ height: 32, width: 'auto', objectFit: 'contain' }} />
                )}
                <p style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 1, margin: 0 }}>{profile?.clinics?.name}</p>
              </div>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 2px', letterSpacing: -0.5 }}>Home Exercise Program</h1>
              <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>{profile?.full_name}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              {startDate && <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 2px' }}>Start: {new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>}
              {programEnd && <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 2px' }}>End: {programEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>}
              <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>Printed {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
            </div>
          </div>

          {/* Weekly schedule chart */}
          {(assignments.length > 0 || interventionAssignments.length > 0) && (
            <div style={{ marginBottom: '2rem', overflowX: 'auto' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px' }}>Weekly Schedule</p>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '32%' }} />
                  {DAY_SHORT.map((_, i) => <col key={i} style={{ width: '9.7%' }} />)}
                  <col style={{ width: '10%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ ...headerCellStyle, textAlign: 'left', paddingLeft: 12 }}>Exercise</th>
                    {DAY_SHORT.map(day => (
                      <th key={day} style={headerCellStyle}>{day}</th>
                    ))}
                    <th style={headerCellStyle}>Until</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((a, i) => (
                    <tr key={a.id} style={{ background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                      <td style={{ ...cellStyle, textAlign: 'left', paddingLeft: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                          <div>
                            <p style={{ fontWeight: 600, fontSize: 12, color: '#111827', margin: 0 }}>{a.exercises.title}</p>
                            <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>
                              {[
                                (a.sets || a.exercises.sets) && `${a.sets || a.exercises.sets} sets`,
                                (a.reps || a.exercises.reps) && `${a.reps || a.exercises.reps} reps`,
                                (a.duration_seconds || a.exercises.duration_seconds) && `${a.duration_seconds || a.exercises.duration_seconds}s`
                              ].filter(Boolean).join(' · ')}
                            </p>
                          </div>
                        </div>
                      </td>
                      {DAY_SHORT.map((_, dayIndex) => (
                        <td key={dayIndex} style={{ ...cellStyle, background: isScheduledOnDay(a, dayIndex) ? '#eef2ff' : i % 2 === 0 ? 'white' : '#fafafa' }}>
                          {isScheduledOnDay(a, dayIndex) && (
                            <div style={{ width: 14, height: 14, border: '1.5px solid #6366f1', borderRadius: 3, margin: '0 auto' }} />
                          )}
                        </td>
                      ))}
                      <td style={{ ...cellStyle, fontSize: 11, color: '#9ca3af' }}>
                        {endDateLabel(a) || '—'}
                      </td>
                    </tr>
                  ))}

                  {interventionAssignments.length > 0 && assignments.length > 0 && (
                    <tr>
                      <td colSpan={9} style={{ ...cellStyle, background: '#f3f4f6', padding: '6px 12px', textAlign: 'left' }}>
                        <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: 0 }}>Tasks</p>
                      </td>
                    </tr>
                  )}

                  {interventionAssignments.map((a, i) => (
                    <tr key={a.id} style={{ background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                      <td style={{ ...cellStyle, textAlign: 'left', paddingLeft: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {a.interventions.media_url && (
                            <img src={a.interventions.media_url} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                          )}
                          <p style={{ fontWeight: 600, fontSize: 12, color: '#111827', margin: 0 }}>{a.interventions.title}</p>
                        </div>
                      </td>
                      {DAY_SHORT.map((_, dayIndex) => (
                        <td key={dayIndex} style={{ ...cellStyle, background: isScheduledOnDay(a, dayIndex) ? '#f0fdf4' : i % 2 === 0 ? 'white' : '#fafafa' }}>
                          {isScheduledOnDay(a, dayIndex) && (
                            <div style={{ width: 14, height: 14, border: '1.5px solid #059669', borderRadius: 3, margin: '0 auto' }} />
                          )}
                        </td>
                      ))}
                      <td style={{ ...cellStyle, fontSize: 11, color: '#9ca3af' }}>
                        {endDateLabel(a) || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Exercise detail cards */}
          <div style={{ pageBreakBefore: 'always' }} />
          {assignments.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px' }}>Exercise Details</p>
              <div style={{ display: 'grid', gridTemplateColumns: assignments.length === 1 ? '1fr' : '1fr 1fr', gap: 12 }}>
                {assignments.map((a, i) => (
                  <div key={a.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', breakInside: 'avoid', background: 'white' }}>
                    {a.exercises.media_url && (
                      <img src={a.exercises.media_url} alt={a.exercises.title} style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block', borderBottom: '1px solid #e5e7eb' }} />
                    )}
                    <div style={{ padding: '12px' }}>
                      <p style={{ fontWeight: 700, fontSize: 14, color: '#111827', margin: '0 0 6px' }}>{i + 1}. {a.exercises.title}</p>
                      {a.exercises.instructions && (
                        <p style={{ fontSize: 12, color: '#374151', margin: '0 0 6px', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{a.exercises.instructions}</p>
                      )}
                      {a.clinician_note && (
                        <p style={{ fontSize: 12, color: '#6366f1', margin: '6px 0 0', fontStyle: 'italic', paddingTop: 6, borderTop: '1px dashed #e5e7eb' }}>📝 {a.clinician_note}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Task details */}
          {interventionAssignments.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px' }}>Task Details</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {interventionAssignments.map((a, i) => (
                  <div key={a.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px', breakInside: 'avoid', background: 'white' }}>
                    <p style={{ fontWeight: 700, fontSize: 14, color: '#111827', margin: '0 0 4px' }}>{i + 1}. {a.interventions.title}</p>
                    {a.interventions.instructions && (
                      <p style={{ fontSize: 12, color: '#374151', margin: '4px 0 0', lineHeight: 1.6 }}>{a.interventions.instructions}</p>
                    )}
                    {a.clinician_note && (
                      <p style={{ fontSize: 12, color: '#6366f1', margin: '6px 0 0', fontStyle: 'italic' }}>📝 {a.clinician_note}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
            <p style={{ fontSize: 11, color: '#9ca3af', margin: 0 }}>Generated by Complete PT · completept.app</p>
            <p style={{ fontSize: 11, color: '#9ca3af', margin: 0 }}>{profile?.clinics?.name}</p>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .page { padding: 0 !important; max-width: 100% !important; }
          @page { margin: 1.5cm; size: A4 landscape; }
        }
      `}</style>
    </>
  )
}
